import { DatasetObjectQueryColumn } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { JsonObject } from 'type-fest';
import { formatDateTime } from '../../common/formatHelper';
import useFormatter, { Formatter } from '../../common/useFormatter';
import ButtonLocateLocation from '../../components/ButtonLocateLocation';
import { ColumnConfig } from '../../components/DatasetTable';
import FormattedValue from '../../components/FormattedValue';
import TagVelocityCategory from '../../components/TagVelocityCategory';
import ActualityEntityTag from '../../feed/components/ActualityIEntityTag';
import { actualityHqFieldTypesBase } from '../../feed/store/actuality.hq.default';
import { actualityEffectiveFilterConfig } from '../../feed/store/actuality.state';
import { getColumnRenderType } from './aiAgent.helper';

type Renderer = ColumnConfig<JsonObject>['render'];

function getColumnRenderer(
  column: DatasetObjectQueryColumn,
  formatter: Formatter,
): Renderer {
  const typedRender = getColumnRenderType(column);

  switch (typedRender) {
    case 'agentId':
      return (agentId: string) => (
        <ActualityEntityTag
          entity="agentId"
          title={agentId}
          filters={{ agentId }}
        />
      );

    case 'job':
      return (jobId: string, row) => (
        <ActualityEntityTag
          entity="job"
          title={jobId}
          filters={{
            agentId: (row['agentId'] ?? row['agent_id']) as string,
            jobId,
          }}
        />
      );
    case 'item':
      return (value: string) => (
        <ActualityEntityTag
          entity="item"
          title={value}
          filters={{ sku: value }}
        />
      );
    case 'event':
      return (value: string) => (
        <ActualityEntityTag
          entity="event"
          title={value}
          filters={{ eventId: value }}
        />
      );

    case 'location':
      return (value: string) => <ButtonLocateLocation locationId={value} />;

    case 'velocity':
      return (value: string) => <TagVelocityCategory rank={value} />;

    case 'weight':
      return (value: number) => (
        <FormattedValue value={formatter.formatWeight(value)} />
      );

    case 'volume':
      return (value: number) => (
        <FormattedValue value={formatter.formatVolume(value)} />
      );

    case 'quantity':
      return (value: number) => (
        <FormattedValue value={formatter.formatInteger(value)} />
      );

    case 'percent':
      return (value: number) => (
        <FormattedValue value={formatter.formatShare(value / 100)} />
      );

    case 'currency':
      return (value: number) => (
        <FormattedValue value={formatter.formatCurrency(value, 2)} />
      );

    case 'compliance':
      return (value: string) => <TagVelocityCategory rank={value} />;

    case 'distance':
      return (value: number) => (
        <FormattedValue value={formatter.formatDistance(value, 2)} />
      );

    case 'duration':
      return (value: number) => formatter.formatTimeSpan(value, 2).fullString;

    case 'localDateTime':
      return (value: number) => formatDateTime(new Date(value));

    default:
      return null;
  }
}

export type AiAgentResponseTableConfigParams = {
  columns: DatasetObjectQueryColumn[];
};

function useAiAgentResponseTableConfig(
  params: AiAgentResponseTableConfigParams,
): ColumnConfig<JsonObject>[] {
  const { t } = useTranslation('dataset');
  const formatter = useFormatter();
  const filterConfig = useRecoilValue(actualityEffectiveFilterConfig);

  const filterMap = _.keyBy(filterConfig, f => f.type);
  const columnsConfig: ColumnConfig<JsonObject>[] = _.map(
    params.columns,
    column => ({
      field: column.name,
      title: _.startCase(_.replace(column.name, '_', ' ')),
      hasFilter: false,
      render: getColumnRenderer(column, formatter),
      hasDefaultEmptyRender: true,
      dynamicPresetConfig: _.has(filterMap, _.camelCase(column.name))
        ? {
            presetType: _.camelCase(column.name),
            dataType: actualityHqFieldTypesBase[_.camelCase(column.name)],
          }
        : null,
    }),
  ) as any;
  return columnsConfig;
}
export default useAiAgentResponseTableConfig;
